/**
 * FossilPenaltyModal.tsx — R13 Bug #8
 *
 * Centered modal for punish_fossils headline effect.
 * Replaces the micro-toast from NewsTickerStrip for this specific effect.
 *
 * Spec:
 *   - scrim + center dialog (mirrors CardEffectAckModal pattern)
 *   - NO auto-dismiss (requires Acknowledge button click)
 *   - NO backdrop close, NO Esc close
 *   - Shows headline title + body + ☠️ +2 pollution delta note
 *
 * VISIBLE WHEN: currentHeadline?.effect === "punish_fossils"
 * TEST PATH: endTurn round-wrap → headline punish_fossils → modal appears.
 */

import type React from "react";
import { useGameStore } from "../../store/game-store.js";
import styles from "./FossilPenaltyModal.module.css";

export function FossilPenaltyModal(): React.ReactElement | null {
  const currentHeadline = useGameStore((s) => s.currentHeadline);
  const dismissHeadline = useGameStore((s) => s.dismissHeadline);
  const boardAnimationActive = useGameStore((s) => s.boardAnimationActive);

  // Only render for punish_fossils, and only after board anim settles
  if (currentHeadline === null) return null;
  if (currentHeadline.effect !== "punish_fossils") return null;
  if (boardAnimationActive) return null;

  return (
    // Scrim — click-through blocked (no onClick)
    <div className={styles.scrim} data-testid="fossil-penalty-scrim">
      <dialog
        open
        className={styles.dialog}
        data-testid="fossil-penalty-dialog"
        aria-labelledby="fossil-penalty-title"
        aria-modal="true"
        // R13 Codex-C MED: onCancel on element handles Esc reliably; previous
        // useEffect+ref pattern missed the listener when dialog wasn't mounted yet.
        onCancel={(e) => e.preventDefault()}
      >
        <div className={styles.panel}>
          <div
            className={styles.title}
            id="fossil-penalty-title"
            data-testid="fossil-penalty-title"
          >
            ☠️ FOSSIL PENALTY
          </div>

          {/* R15 carry-forward (R13 /goal Risk 4): show "Headline Effect"
              without the era number — currentHeadline.era is the era at which
              the headline was authored, not the current game era, which
              confused players when era 1 headlines fired in era 3. */}
          <div className={styles.subtitle}>Headline Effect</div>

          <div className={styles.body} data-testid="fossil-penalty-body">
            {currentHeadline.title}
          </div>

          {currentHeadline.body.length > 0 && (
            <div className={styles.body} style={{ fontSize: "16px", opacity: 0.8 }}>
              {currentHeadline.body}
            </div>
          )}

          <ul className={styles.penaltyList}>
            <li className={styles.penaltyRow} data-testid="fossil-penalty-delta">
              ☠️ +2 Pollution — all fossil facility owners
            </li>
          </ul>

          <button
            type="button"
            className={styles.ackBtn}
            data-testid="btn-fossil-penalty-acknowledge"
            onClick={dismissHeadline}
          >
            ✓ Acknowledge
          </button>
        </div>
      </dialog>
    </div>
  );
}
